'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/buttons/button";
import { redeemHolidayGiftCard } from '@/lib/actions';
import { useQueryClient } from '@tanstack/react-query';
import { LoadingSpinner } from "./spinner";
import { CheckOnboardingState } from './check-onboarding-state';
import { useOnboardingState } from '@/hooks/useOnboardingState';

export const HolidayGiftCardDialog = () => {
    const router = useRouter();
    const searchParams = useSearchParams();
    const [open, setOpen] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useQueryClient();
    const { isLoading: isCheckingOnboarding, isOnboarded } = useOnboardingState();

    const handleClose = () => {
        const params = new URLSearchParams(searchParams);
        params.delete('holiday-gc');
        router.replace(`?${params.toString()}`);
        setOpen(false);
    };

    const handleRedeem = async () => {
        try {
            setIsLoading(true);
            const result = await redeemHolidayGiftCard();

            if (!result.alreadyRedeemed) {
                queryClient.invalidateQueries({ queryKey: ['balance'] });
            }

            const params = new URLSearchParams(searchParams);
            params.delete('holiday-gc');
            router.replace('/company');
        } catch (error) {
            console.error('Error redeeming gift card:', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (isCheckingOnboarding) {
        return (
            <div className="fixed inset-0 bg-black/50 z-toast flex items-center justify-center">
                <LoadingSpinner />
            </div>
        );
    }

    if (!isOnboarded) {
        return <CheckOnboardingState />;
    }

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="flex flex-col bg-gray-100 sm:max-w-[500px] z-modal md:rounded-xl items-center justify-center text-center gap-6" onOpenAutoFocus={(e) => e.preventDefault()}>
                <DialogHeader className="flex md:w-[315px] flex-col items-center justify-center">
                    <DialogTitle className="text-2xl font-semibold mb-2 text-center">
                        Holiday Free Report 🎉
                    </DialogTitle>
                    <DialogDescription className="text-center text-black/50">
                        Redeem your holiday free report to access buyer insights on over 1,500 companies
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col items-center justify-center bg-natural-50 border border-black-50 text-black text-center rounded-[10px] h-[200px] w-full p-10">
                    <div className="text-4xl mb-4">🎁</div>
                    <div className="text-lg font-medium">Special Holiday Reward</div>
                </div>
                <div className="flex flex-col md:flex-row w-full justify-between gap-4 md:gap-4 px-2">
                    <Button
                        onClick={handleClose}
                        variant="outline"
                        disabled={isLoading}
                        className="font-medium text-xs"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRedeem}
                        disabled={isLoading}
                        className="font-medium text-xs"
                    >
                        {isLoading ? <LoadingSpinner /> : 'Redeem Now'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
} 